import { detectDeviceClass, type DeviceClass } from './deviceClass.js';

export type OsBucket = 'windows' | 'macos' | 'ios' | 'android' | 'linux' | 'chromeos' | 'other';

export type BrowserBucket = 'chrome' | 'safari' | 'firefox' | 'edge' | 'other';

export interface UserAgentBrand {
  brand: string;
  version: string;
}

export interface ClientEnvSource {
  ua?: string;
  brands?: UserAgentBrand[];
  uaDataPlatform?: string;
  maxTouchPoints?: number;
  screenW?: number;
  screenH?: number;
  dpr?: number;
  cores?: number;
  deviceMemory?: number;
}

export interface ClientEnv {
  os: OsBucket;
  browser: BrowserBucket;
  browserVersion: number | null;
  deviceClass: DeviceClass;
  screenW: number | null;
  screenH: number | null;
  dpr: number | null;
  cores: number | null;
  deviceMemory: number | null;
}

interface NavigatorWithUaData extends Navigator {
  userAgentData?: {
    brands?: UserAgentBrand[];
    platform?: string;
  };
  deviceMemory?: number;
}

function positiveFinite(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

function major(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function osFromPlatform(platform: string): OsBucket | null {
  if (/chrome\s*os|chromeos|cros/i.test(platform)) return 'chromeos';
  if (/windows/i.test(platform)) return 'windows';
  if (/macos|macintosh|macintel/i.test(platform)) return 'macos';
  if (/iphone|ipad|ipod|ios/i.test(platform)) return 'ios';
  if (/android/i.test(platform)) return 'android';
  if (/linux/i.test(platform)) return 'linux';
  return null;
}

function detectOs(ua: string, platform: string, maxTouchPoints: number): OsBucket {
  const platformBucket = osFromPlatform(platform);
  if (platformBucket) {
    // Desktop-UA iPadOS reports macOS/MacIntel through both APIs.
    if (platformBucket === 'macos' && maxTouchPoints > 1) return 'ios';
    return platformBucket;
  }

  if (/iPhone|iPod|iPad/i.test(ua)) return 'ios';
  if (/Macintosh/i.test(ua) && maxTouchPoints > 1) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  if (/CrOS/i.test(ua)) return 'chromeos';
  if (/Windows/i.test(ua)) return 'windows';
  if (/Macintosh/i.test(ua)) return 'macos';
  if (/Linux|X11/i.test(ua)) return 'linux';
  return 'other';
}

function browserFromBrands(
  brands: UserAgentBrand[],
): { browser: BrowserBucket; browserVersion: number | null } | null {
  const edge = brands.find((entry) => /microsoft edge/i.test(entry.brand));
  if (edge) return { browser: 'edge', browserVersion: major(edge.version) };

  const chrome = brands.find((entry) => /google chrome/i.test(entry.brand));
  if (chrome) return { browser: 'chrome', browserVersion: major(chrome.version) };

  // A Chromium-only brand is not enough to call the browser Chrome. Fall back
  // to the UA so Edge, Opera, Samsung Internet, and other shells stay honest.
  return null;
}

function detectBrowser(
  ua: string,
  brands: UserAgentBrand[],
): { browser: BrowserBucket; browserVersion: number | null } {
  const branded = browserFromBrands(brands);
  if (branded) return branded;

  let match = ua.match(/(?:EdgA|EdgiOS|Edg)\/(\d+)/i);
  if (match) return { browser: 'edge', browserVersion: major(match[1]) };

  if (/(?:OPR|SamsungBrowser)\//i.test(ua)) {
    return { browser: 'other', browserVersion: null };
  }

  match = ua.match(/(?:FxiOS|Firefox)\/(\d+)/i);
  if (match) return { browser: 'firefox', browserVersion: major(match[1]) };

  match = ua.match(/(?:CriOS|Chrome)\/(\d+)/i);
  if (match) return { browser: 'chrome', browserVersion: major(match[1]) };

  if (/Safari\//i.test(ua)) {
    match = ua.match(/Version\/(\d+)/i);
    return { browser: 'safari', browserVersion: major(match?.[1]) };
  }

  return { browser: 'other', browserVersion: null };
}

function liveSource(): ClientEnvSource {
  try {
    // Node also exposes a small navigator object. It is not a client browser,
    // so require window before treating any globals as analytics inputs.
    if (typeof window === 'undefined') return {};
    const nav = typeof navigator === 'undefined' ? null : (navigator as NavigatorWithUaData);
    const currentScreen = typeof screen === 'undefined' ? null : screen;
    return {
      ua: nav?.userAgent,
      brands: nav?.userAgentData?.brands,
      uaDataPlatform: nav?.userAgentData?.platform,
      maxTouchPoints: nav?.maxTouchPoints,
      screenW: currentScreen?.width,
      screenH: currentScreen?.height,
      dpr: window.devicePixelRatio,
      cores: nav?.hardwareConcurrency,
      deviceMemory: nav?.deviceMemory,
    };
  } catch {
    return {};
  }
}

/** Detect a coarse, non-identifying client environment. Never throws. */
export function detectClientEnv(src: ClientEnvSource = {}): ClientEnv {
  try {
    const live = liveSource();
    const source = { ...live, ...src };
    const ua = typeof source.ua === 'string' ? source.ua : '';
    const platform = typeof source.uaDataPlatform === 'string' ? source.uaDataPlatform : '';
    const brands = Array.isArray(source.brands)
      ? source.brands.filter(
          (entry): entry is UserAgentBrand =>
            typeof entry?.brand === 'string' && typeof entry?.version === 'string',
        )
      : [];
    const maxTouchPoints = positiveFinite(source.maxTouchPoints) ?? 0;
    const screenW = positiveFinite(source.screenW);
    const screenH = positiveFinite(source.screenH);
    const shortSide = screenW !== null && screenH !== null ? Math.min(screenW, screenH) : 0;
    const { browser, browserVersion } = detectBrowser(ua, brands);

    return {
      os: detectOs(ua, platform, maxTouchPoints),
      browser,
      browserVersion,
      deviceClass: detectDeviceClass({ ua, maxTouchPoints, shortSide }),
      screenW,
      screenH,
      dpr: positiveFinite(source.dpr),
      cores: positiveFinite(source.cores),
      deviceMemory: positiveFinite(source.deviceMemory),
    };
  } catch {
    return {
      os: 'other',
      browser: 'other',
      browserVersion: null,
      deviceClass: 'desktop',
      screenW: null,
      screenH: null,
      dpr: null,
      cores: null,
      deviceMemory: null,
    };
  }
}
